import React from "react";
import { useTheme } from "../../context/ThemeContext";

// Helper function to calculate grid layout
const calculateGridLayout = (count) => {
  if (count <= 1) return "grid-cols-1";
  if (count === 2) return "grid-cols-2";
  if (count <= 4) return "grid-cols-2 grid-rows-2";
  if (count <= 6) return "grid-cols-3 grid-rows-2";
  if (count <= 9) return "grid-cols-3 grid-rows-3";
  if (count <= 12) return "grid-cols-3 grid-rows-4";
  return "grid-cols-3 grid-rows-auto"; // More than 12, scrollable
};

// Helper function to calculate circle sizes
const calculateCircleSize = (queueIndex, totalQueues) => {
  const maxScreenWidth = window.innerWidth; // Ancho de la pantalla
  const baseSize = Math.min(maxScreenWidth / totalQueues, 100); // Escala con el tamaño
  const scaleFactor = Math.max(0.5, 1 - totalQueues * 0.05); // Escalado dinámico
  return baseSize * scaleFactor;
};

const calculateSmallCircleSize = (queueIndex, totalQueues) => {
  return calculateCircleSize(queueIndex, totalQueues) * 0.4; // Proporcional al círculo principal
};

const RealtimeQueues = ({ queues, loading = true }) => {
  const totalQueues = queues.length;
  const { isDarkMode } = useTheme();

  const renderValueOrSkeleton = (value) =>
    loading ? (
      <div className="w-8 h-4 bg-gray-300 rounded animate-pulse"></div>
    ) : (
      value
    );

  return (
    <div
      className={`w-full h-full grid ${calculateGridLayout(totalQueues)} gap-4`}
    >
      {queues.map((queue, index) => {
        const circleSize = calculateCircleSize(index, totalQueues);
        const smallCircleSize = calculateSmallCircleSize(index, totalQueues);
        const queueName =
          queue?.id && queue?.id?.startsWith("20") // Verificar si el ID empieza por "20"
            ? queue?.id?.slice(5) // Eliminar los primeros 5 dígitos
            : queue?.id || `Queue ${index + 1}`; // Usar un valor por defecto si no cumple la condición

        return (
          <div
            key={queue.name}
            className="bg-white shadow-lg rounded-lg flex flex-col p-4 hover:shadow-xl transition-shadow relative dark:bg-primary-dark"
          >
            {/* Fila 1: Título */}
            <div className="flex justify-between items-center">
              <div className="font-semibold text-gray-800 text-lg dark:text-white">
                {queueName?.toUpperCase()}
              </div>
            </div>

            {/* Fila 2: Círculo */}
            {/* Fila 2: Círculo */}
            <div className="flex flex-1 items-center justify-center">
              {loading ? (
                // Círculo plano con efecto skeleton
                <div
                  className={`relative flex items-center justify-center rounded-full ${
                    isDarkMode ? "bg-gray-700" : "bg-gray-300"
                  } animate-pulse`}
                  style={{
                    width: `${circleSize}px`,
                    height: `${circleSize}px`,
                  }}
                ></div>
              ) : (
                // Círculo con contenido normal
                <div
                  className={`relative flex items-center justify-center rounded-full ${
                    queue.callsInQueue > 0
                      ? isDarkMode
                        ? "bg-purple-900"
                        : "bg-purple-100"
                      : isDarkMode
                      ? "bg-gray-800"
                      : "bg-gray-200"
                  }`}
                  style={{
                    width: `${circleSize}px`,
                    height: `${circleSize}px`,
                    animation:
                      queue.callsInQueue > 0
                        ? "call 1.5s ease-in-out infinite"
                        : "none",
                  }}
                >
                  {/* Simulación del borde */}
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      boxShadow: `0 0 0 4px ${
                        queue.callsInQueue > 0
                          ? isDarkMode
                            ? "#6b21a8" // Un morado más oscuro para el borde en dark mode
                            : "#a855f7"
                          : isDarkMode
                          ? "#555" // Gris oscuro para el borde en dark mode
                          : "#ccc"
                      }`,
                    }}
                  ></div>

                  {/* Contenido del círculo */}
                  <div
                    className={`text-center font-bold text-3xl ${
                      queue.callsInQueue > 0
                        ? isDarkMode
                          ? "text-purple-400"
                          : "text-purple-600"
                        : isDarkMode
                        ? "text-gray-400"
                        : "text-gray-500"
                    }`}
                  >
                    {renderValueOrSkeleton(queue.callsInQueue)}
                  </div>

                  {/* Pequeño círculo */}
                  <div
                    className={`absolute flex items-center justify-center rounded-full ${
                      queue.freeAgents > 0
                        ? isDarkMode
                          ? "bg-green-800"
                          : "bg-green-200"
                        : isDarkMode
                        ? "bg-gray-700"
                        : "bg-gray-200"
                    }`}
                    style={{
                      width: `${smallCircleSize}px`,
                      height: `${smallCircleSize}px`,
                      bottom: `-${smallCircleSize / 3}px`,
                      right: `-${smallCircleSize / 3}px`,
                      boxShadow: `0 0 0 2px ${
                        queue.freeAgents > 0
                          ? isDarkMode
                            ? "rgba(16, 185, 129, 1)" // Verde oscuro para dark mode
                            : "rgba(34, 197, 94, 1)"
                          : isDarkMode
                          ? "#555" // Gris oscuro para dark mode
                          : "#ccc"
                      }`,
                    }}
                  >
                    <span
                      className={`text-sm font-semibold ${
                        queue.freeAgents > 0
                          ? isDarkMode
                            ? "text-green-400"
                            : "text-green-700"
                          : isDarkMode
                          ? "text-gray-300"
                          : "text-gray-700"
                      }`}
                    >
                      {renderValueOrSkeleton(queue.freeAgents)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Fila 3: Datos */}
            <div className="flex flex-wrap justify-evenly items-center mt-6 text-md text-gray-600 border-t">
              {/* Dato: Llamadas atendidas */}
              <div className="flex flex-col items-center text-center px-1">
                <span className="text-xs text-gray-500 dark:text-gray-200">
                  Attended
                </span>
                <span className="font-bold text-green-500">
                  {renderValueOrSkeleton(queue.attendedCalls)}
                </span>
              </div>

              {/* Dato: Llamadas abandonadas */}
              <div className="flex flex-col items-center text-center px-1">
                <span className="text-xs text-gray-500 dark:text-gray-200">
                  Non Attended
                </span>
                <span className="font-bold text-red-500">
                  {renderValueOrSkeleton(queue.abandonedCalls)}
                </span>
              </div>

              {/* Dato: Llamadas perdidas */}

              {/* Dato: Tiempo promedio de conversación */}
              <div className="flex flex-col items-center text-center px-1">
                <span className="text-xs text-gray-500 dark:text-gray-200">
                  AVG Talk
                </span>
                <span className="font-bold text-gray-500 dark:text-white">
                  {renderValueOrSkeleton(queue.avgTalkTime)}
                </span>
              </div>

              {/* Dato: Tiempo promedio de espera */}
              <div className="flex flex-col items-center text-center px-1">
                <span className="text-xs text-gray-500 dark:text-gray-200">
                  AVG Wait
                </span>
                <span className="font-bold text-gray-500 dark:text-white">
                  {renderValueOrSkeleton(queue.avgWaitTime)}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RealtimeQueues;
